"use client";

import { memo, useEffect, useState } from "react";

const SANITIZE_CONFIG = {
  ADD_TAGS: ["iframe"],
  ADD_ATTR: [
    "allow",
    "allowfullscreen",
    "frameborder",
    "scrolling",
    "src",
    "height",
    "width",
    "title",
    "referrerpolicy",
    "sandbox",
  ],
};

// Memoized inner component to prevent re-renders from parent state changes
const HtmlContent = memo(({ html, className }: { html: string; className: string }) => (
  <div className={className} dangerouslySetInnerHTML={{ __html: html }} aria-label="Rich text content" tabIndex={0} />
));
HtmlContent.displayName = "HtmlContent";

export const HtmlDisplay = memo(({ content, className = "" }: { content: string; className?: string }) => {
  const [sanitizedHtml, setSanitizedHtml] = useState("");
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
    // Dynamic import of DOMPurify to avoid SSR issues
    import("dompurify").then((DOMPurify) => {
      const cleanHtml = DOMPurify.default.sanitize(content, SANITIZE_CONFIG);
      setSanitizedHtml(cleanHtml);
    });
  }, [content]);

  const combinedClassName = `prose max-w-none ${className}`;

  // On server or before hydration, render unsanitized (Next.js will replace on client)
  if (!isClient) {
    return <HtmlContent html={content} className={combinedClassName} />;
  }

  // On client, render sanitized HTML
  return <HtmlContent html={sanitizedHtml} className={combinedClassName} />;
});
HtmlDisplay.displayName = "HtmlDisplay";
